import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { AlertTriangle, X, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { theme } from '../../styles/theme';
import api from '../../services/api';

/**
 * UpgradeBanner
 * Exibe banner proativo quando o tenant está a >= 80% do limite de conversas.
 * Busca usage e tier do tenant e calcula o percentual.
 * Dispensável pelo usuário (salvo em localStorage por 24h).
 */
const UpgradeBanner = ({ tenantId }) => {
  const { t } = useTranslation('billing');
  const [data, setData]       = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    const key = `upgrade_banner_dismissed_${tenantId}_${new Date().toISOString().slice(0, 10)}`;
    if (localStorage.getItem(key)) { setDismissed(true); return; }

    api.get(`/usage/${tenantId}`)
      .then(usageRes => {
        const usage = usageRes?.data;
        if (!usage) return;
        const percent = usage.usagePercent ?? 0;
        if (percent < 80) return;
        setData({
          percent,
          reason:         usage.usageReason || 'conversations',
          conversations:  usage.conversations  || 0,
          effectiveLimit: usage.limit          || 0,
        });
      })
      .catch(() => {});
  }, [tenantId]);

  const dismiss = () => {
    const key = `upgrade_banner_dismissed_${tenantId}_${new Date().toISOString().slice(0, 10)}`;
    localStorage.setItem(key, '1');
    setDismissed(true);
  };

  if (!data || dismissed) return null;

  const isOver90  = data.percent >= 90;
  const isOver100 = data.percent >= 100;
  const isTokens  = data.reason === 'tokensInput' || data.reason === 'tokensOutput';

  const titleKey = isOver100
    ? (isTokens ? 'upgradeBanner.tokenLimitReached'    : 'upgradeBanner.limitReached')
    : (isTokens ? 'upgradeBanner.tokenLimitWarning'    : 'upgradeBanner.limitWarning');

  const descKey = isOver100
    ? (isTokens ? 'upgradeBanner.tokenLimitReachedDesc' : 'upgradeBanner.limitReachedDesc')
    : (isTokens ? 'upgradeBanner.tokenLimitWarningDesc' : 'upgradeBanner.limitWarningDesc');

  const descValues = isTokens
    ? { percent: data.percent }
    : { conversations: data.conversations, limit: data.effectiveLimit };

  return (
    <Banner $level={isOver100 ? 'critical' : isOver90 ? 'warning' : 'info'}>
      <BannerIcon>
        {isOver100 ? '🚨' : isOver90 ? '⚠️' : <TrendingUp size={16} />}
      </BannerIcon>
      <BannerBody>
        <BannerTitle>
          {t(titleKey, { percent: data.percent, defaultValue: `${data.percent}% do limite atingido` })}
        </BannerTitle>
        <BannerText>
          {t(descKey, { ...descValues, defaultValue: `${data.percent}% do limite usado este mês.` })}
        </BannerText>
      </BannerBody>
      <BannerActions>
        <UpgradeBtn href={`/billing/${tenantId}`}>
          <TrendingUp size={13} />
          {t('upgradeBanner.upgradeBtn', { defaultValue: 'Fazer upgrade' })}
        </UpgradeBtn>
        <DismissBtn onClick={dismiss} title={t('upgradeBanner.dismissToday', { defaultValue: 'Dispensar por hoje' })}>
          <X size={14} />
        </DismissBtn>
      </BannerActions>
    </Banner>
  );
};

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  border-radius: ${theme.radii.lg};
  margin-bottom: ${theme.spacing[4]};
  border: 1.5px solid ${p =>
    p.$level === 'critical' ? theme.colors.danger :
    p.$level === 'warning'  ? theme.colors.warning :
    theme.colors.primary};
  background: ${p =>
    p.$level === 'critical' ? '#fff5f5' :
    p.$level === 'warning'  ? theme.colors.warningLight :
    theme.colors.primaryLight};
`;
const BannerIcon = styled.div`font-size: 18px; flex-shrink: 0; display: flex; align-items: center;`;
const BannerBody = styled.div`flex: 1; min-width: 0;`;
const BannerTitle = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;
const BannerText = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textSecondary};
  margin-top: 2px;
`;
const BannerActions = styled.div`display: flex; align-items: center; gap: ${theme.spacing[2]}; flex-shrink: 0;`;
const UpgradeBtn = styled.a`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: ${theme.colors.primary};
  color: white;
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.semibold};
  text-decoration: none;
  white-space: nowrap;
  &:hover { background: ${theme.colors.primaryHover}; }
`;
const DismissBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  padding: 2px;
  display: flex;
  align-items: center;
  &:hover { color: ${theme.colors.textPrimary}; }
`;

export default UpgradeBanner;
